import { db, auth } from '../config/firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';

// FEATURE FLAG - Set to true when Stage 3 & 4 are ready to launch
const STAGE_3_4_ENABLED = false;

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  console.log('AppProvider rendering...');
  
  // User state
  const [currentUser, setCurrentUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [authLoading, setAuthLoading] = useState(true); // NEW: Loading state
  
  // UI state
  const [showLanding, setShowLanding] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showRevisionPaymentModal, setShowRevisionPaymentModal] = useState(false);
  const [showRevisionRequestModal, setShowRevisionRequestModal] = useState(false);
  const [showUploadFormsModal, setShowUploadFormsModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showSampleViewer, setShowSampleViewer] = useState(false);
  const [showFeasibilityViewer, setShowFeasibilityViewer] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  
  // Project state
  const [paymentProject, setPaymentProject] = useState(null);
  const [revisionProject, setRevisionProject] = useState(null);
  const [revisionRequestProject, setRevisionRequestProject] = useState(null);
  const [uploadFormsProject, setUploadFormsProject] = useState(null);
  const [revisionData, setRevisionData] = useState(null);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);

  // In-app messaging state
  const [inAppMessages, setInAppMessages] = useState([]);
  const [dismissedMessages, setDismissedMessages] = useState([]);

  // Constants (keeping all your existing constants exactly the same)
  const typeNames = {
    'single': 'Single Dwelling',
    'duplex': 'Duplex',
    'townhouse': 'Townhouse Development',
    'secondary': 'Secondary Dwelling',
    'minor-renovation': 'Minor Alterations & Additions',
    'major-renovation': 'Major Alterations & Additions'
  };

  const developmentSettings = {
    single: { maxStoreys: 3, basixRequired: true },
    duplex: { maxStoreys: 3, basixRequired: true },
    townhouse: { maxUnits: 10, basixRequired: true },
    secondary: { maxArea: 60, basixRequired: true },
    'minor-renovation': { maxCost: 150000, basixRequired: false },
    'major-renovation': { maxCost: 500000, basixRequired: true }
  };

  const pricing = {
    single: { 1: '$199', 2: '$550', 3: 'Quote', 4: 'Quote' },
    duplex: { 1: '$249', 2: '$550', 3: 'Quote', 4: 'Quote' },
    townhouse: { 1: '$499-$799', 2: '$799-$1,099', 3: 'Quote', 4: 'Quote' },
    secondary: { 1: '$149', 2: '$299', 3: 'Quote', 4: 'Quote' },
    'minor-renovation': { 1: '$149', 2: '$299', 3: 'Quote', 4: 'Quote' },
    'major-renovation': { 1: '$199', 2: '$349', 3: 'Quote', 4: 'Quote' }
  };

  const documentTypes = {
    stage3: {
      'quote': 'Stage 3 Quote Document',
      'architectural': 'Architectural Plans',
      'structural': 'Structural Engineering',
      'hydraulic': 'Hydraulic/Stormwater',
      'basix': 'BASIX Certificate',
      'survey': 'Survey Plans',
      'geotechnical': 'Geotechnical Report',
      'arborist': 'Arborist Report',
      'heritage': 'Heritage Report',
      'flood': 'Flood Report',
      'bushfire': 'Bushfire Report',
      'see': 'Statement of Environmental Effects',
      'consultant-agreements': 'Consultant Agreements',
      'application-package': 'Application Package',
      'other': 'Other Supporting Documents'
    },
    stage4: {
      'quote': 'Stage 4 Quote Document',
      'architectural-detailed': 'Detailed Architectural - General Plans',
      'structural-detailed': 'Detailed Structural',
      'electrical': 'Electrical Plans',
      'hydraulic-detailed': 'Detailed Hydraulic',
      'landscape': 'Landscape Plans',
      'construction-details': 'Construction Details',
      'joinery-details': 'Joinery Details',
      'wet-areas-details': 'Wet Areas Details',
      'window-door-schedule': 'Window & Door Schedule',
      'material-finishes-fixtures': 'Material, Finishes and Fixtures Schedules',
      'construction-documentation-package': 'Construction Documentation Package',
      'consultant-agreements': 'Consultant Agreements',
      'specifications': 'Specifications',
      'other': 'Other Construction Documents'
    }
  };

  // Load dismissed messages from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('planaidesign_dismissed_messages');
    if (saved) {
      try {
        setDismissedMessages(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading dismissed messages:', e);
      }
    }
  }, []);

  // NEW: Listen to Firebase Auth state changes instead of just localStorage
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in via Firebase Auth
        const user = {
          id: firebaseUser.uid,
          name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          email: firebaseUser.email,
          registrationDate: firebaseUser.metadata.creationTime || new Date().toISOString()
        };
        
        // Fire conversion tracking for new account creation
      const isNewUser = firebaseUser.metadata.creationTime === firebaseUser.metadata.lastSignInTime;
      if (isNewUser && window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17556788019/r46-CP-59qsbELOm3bNB',
          'value': 1.0,
          'currency': 'AUD'
        });
        console.log('New account conversion tracked');
      }
      
        setCurrentUser(user);
        setShowLanding(false);
        
        // Also save to localStorage for backward compatibility
        localStorage.setItem('planaidesign_user', JSON.stringify(user));
      } else {
        // No Firebase Auth user
        setCurrentUser(null);
        localStorage.removeItem('planaidesign_user');
      }
      
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // In-app messaging functions (keeping exactly the same)
  const dismissMessage = (messageId) => {
    const updated = [...dismissedMessages, messageId];
    setDismissedMessages(updated);
    localStorage.setItem('planaidesign_dismissed_messages', JSON.stringify(updated));
    setInAppMessages(prev => prev.filter(m => m.id !== messageId));
  };

  const checkForInAppMessages = () => {
    console.log('=== CHECKING IN-APP MESSAGES ===');
    console.log('Current user:', currentUser);
    console.log('Projects count:', projects.length);
    
    if (!currentUser) {
      console.log('No current user, skipping messages');
      return;
    }
    
    const userRegistrationDate = new Date(currentUser.registrationDate || currentUser.createdAt || Date.now());
    const daysSinceRegistration = Math.floor((Date.now() - userRegistrationDate) / (1000 * 60 * 60 * 24));
    
    console.log('User registration date:', userRegistrationDate);
    console.log('Days since registration:', daysSinceRegistration);
    
    const userProjects = projects.filter(p => 
      p.email === currentUser.email || 
      p.client === currentUser.name || 
      p.client === currentUser.email
    );
    
    console.log('User projects length:', userProjects.length);
    console.log('Dismissed messages:', dismissedMessages);
    
    const messages = [];
    
    // Always show welcome message for users with no projects
    if (userProjects.length === 0 && !dismissedMessages.includes('welcome')) {
      messages.push({
        id: 'welcome',
        type: 'info',
        title: 'Welcome to planAIdesign!',
        message: 'Start with Stage 1 (Site Investigation & Feasibility) to check if your project is viable before investing in design work.',
        action: { text: 'Create Project', onClick: () => setShowProjectModal(true) },
        dismissible: true
      });
    }
    
    if (daysSinceRegistration >= 2 && daysSinceRegistration <= 3 && userProjects.length === 0 && !dismissedMessages.includes('process')) {
      messages.push({
        id: 'process',
        type: 'info',
        title: 'Not sure where to start?',
        message: 'Most clients begin with Stage 1 to understand site constraints and development potential.',
        action: { text: 'View Process Guide', onClick: () => window.open('https://planaidesign.com.au', '_blank') },
        dismissible: true
      });
    }
    
    console.log('Final messages to set:', messages);
    setInAppMessages(messages);
    console.log('=== END MESSAGE CHECK ===');
  };

  // Check for in-app messages when user or projects change
  useEffect(() => {
    console.log('[MESSAGING] useEffect triggered - User:', !!currentUser, 'Projects:', projects.length);
    checkForInAppMessages();
  }, [currentUser, projects]);

  // FIXED: Load projects with proper query filtering
  useEffect(() => {
    if (!currentUser || authLoading) {
      setProjects([]);
      return;
    }

    console.log('Loading projects for user:', currentUser.email);
    
    let unsubscribe;
    
    if (isAdminUser(currentUser)) {
      // Admin sees all projects
      console.log('Admin user detected, loading all projects');
      unsubscribe = onSnapshot(
        collection(db, 'projects'),
        (snapshot) => {
          const projectsData = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            projectsData.push({
              id: doc.id,
              ...data
            });
          });
          setProjects(projectsData);
          console.log('All projects loaded for admin:', projectsData.length);
        },
        (error) => {
          console.error('Error loading admin projects:', error);
          showNotification('Error', 'Failed to load projects', 'error');
        }
      );
    } else {
      // Regular users only see their own projects
      console.log('Regular user, loading filtered projects');
      const projectsQuery = query(
        collection(db, 'projects'),
        where('userEmail', '==', currentUser.email)
      );
      
      unsubscribe = onSnapshot(
        projectsQuery,
        (snapshot) => {
          const projectsData = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            projectsData.push({
              id: doc.id,
              ...data
            });
          });
          
          // If no projects found with userEmail, try email field
          if (projectsData.length === 0) {
            console.log('No projects found with userEmail, trying email field...');
            const altQuery = query(
              collection(db, 'projects'),
              where('email', '==', currentUser.email)
            );
            
            onSnapshot(altQuery, (altSnapshot) => {
              const altProjectsData = [];
              altSnapshot.forEach((doc) => {
                const data = doc.data();
                altProjectsData.push({
                  id: doc.id,
                  ...data
                });
              });
              setProjects(altProjectsData);
              console.log('Projects loaded with email field:', altProjectsData.length);
            });
          } else {
            setProjects(projectsData);
            console.log('Projects loaded for user:', projectsData.length);
          }
        },
        (error) => {
          console.error('Error loading projects:', error);
          if (error.code === 'permission-denied') {
            showNotification('Error', 'Permission denied. Please contact support.', 'error');
          } else {
            showNotification('Error', 'Failed to load projects', 'error');
          }
        }
      );
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser, authLoading]);

  // Notification function (keeping exactly the same)
  const showNotification = (title, message, type = 'info') => {
    const id = Date.now();
    const now = Date.now();
    const recentNotifications = notifications.filter(n => 
      n.title === title && 
      n.message === message && 
      (now - n.id) < 500
    );
    
    if (recentNotifications.length > 0) {
      return;
    }
    
    setNotifications(prev => [...prev, { id, title, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // UPDATED: Logout function to use Firebase Auth
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setProjects([]);
      setShowLanding(true);
      localStorage.removeItem('planaidesign_user');
      showNotification('Logged Out', 'You have been successfully logged out', 'info');
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback to local logout if Firebase fails
      setCurrentUser(null);
      setProjects([]);
      setShowLanding(true);
      localStorage.removeItem('planaidesign_user');
      showNotification('Logged Out', 'You have been logged out', 'info');
    }
  };

  const isAdminUser = (user = currentUser) => {
    if (!user) {
      try {
        const savedUser = localStorage.getItem('planaidesign_user');
        user = savedUser ? JSON.parse(savedUser) : null;
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        return false;
      }
    }
    
    if (!user || !user.email) {
      return false;
    }
    
    return user.email === 'team@planaidesign.com';
  };

  // All other functions remain EXACTLY THE SAME
  // Payment functions
  const processPayment = (projectId) => {
    setProjects(prev => prev.map(project => {
      if (project.id === projectId) {
        const paymentsMade = (project.paymentsMade || 0) + 1;
        const paymentAmount = project.paymentStructure.paymentAmounts[project.paymentsMade || 0];
        
        const paymentEntry = {
          paymentNumber: paymentsMade,
          amount: paymentAmount,
          description: project.paymentStructure.paymentDescriptions[project.paymentsMade || 0],
          date: new Date().toISOString(),
          method: 'credit-card'
        };
        
        let newStatus = project.status;
        let newPaymentStatus = 'partial';
        
        if (project.stage === 1) {
          if (paymentsMade === 1) {
            newStatus = 'awaiting-balance-payment';
            newPaymentStatus = 'partial';
          } else if (paymentsMade >= project.paymentStructure.totalPayments) {
            newStatus = 'paid';
            newPaymentStatus = 'paid';
          }
        } else if (project.stage === 2) {
          if (paymentsMade === 1) {
            newStatus = 'in-progress';
            newPaymentStatus = 'partial';
          } else if (paymentsMade === 2 && project.paymentStructure.totalPayments === 3) {
            newStatus = 'awaiting-final-payment';
            newPaymentStatus = 'partial';
          } else if (paymentsMade >= project.paymentStructure.totalPayments) {
            newStatus = 'paid';
            newPaymentStatus = 'paid';
          }
        }
        
        return {
          ...project,
          status: newStatus,
          paymentStatus: newPaymentStatus,
          paymentsMade: paymentsMade,
          paymentHistory: [...(project.paymentHistory || []), paymentEntry]
        };
      }
      return project;
    }));
  };

  const processStage34Payment = (projectId, paymentAmount, paymentDescription, paymentType = 'manual') => {
    // Stub for Stage 3/4 payments
    console.log('Processing Stage 3/4 payment:', projectId, paymentAmount);
  };

  // Project functions
  const approveProject = (project) => {
    setProjects(prev => prev.map(p => {
      if (p.id === project.id) {
        let newStatus;
        let message;
        
        if (project.stage === 1) {
          newStatus = 'stage-1-approved';
          message = 'Thank you for approving the feasibility report.';
        } else if (project.stage === 2) {
          newStatus = 'stage-2-approved';
          message = 'Thank you for approving the concept design.';
        } else {
          newStatus = 'approved';
          message = 'Thank you for your approval.';
        }
        
        return {
          ...p,
          status: newStatus,
          approvedDate: new Date().toISOString()
        };
      }
      return p;
    }));
    
    showNotification('Project Approved', 'Project has been approved successfully.', 'success');
  };

  const requestRevision = (project) => {
    showNotification('Revision Requested', 'Revision request functionality coming soon', 'info');
  };

  const requestAdditionalRevision = (project) => {
    requestRevision(project);
  };

  const uploadCompletedForms = (project) => {
    setUploadFormsProject(project);
    setShowUploadFormsModal(true);
  };

  // Pricing calculation functions (all remain the same)
const calculateStage1PaymentStructure = (totalCost) => {
    const costAmount = parseFloat(totalCost.replace('$', ''));
    
    return {
      totalPayments: 1,
      paymentAmounts: [costAmount],
      paymentDescriptions: [
        'Payment to release final documents'
      ],
      paymentModel: 'card-capture', // New: indicates card captured upfront, charged on delivery
      autoChargeAfterDays: 5 // New: auto-charge if not paid within 5 days
    };
  };

  const calculateStage2PaymentStructure = (totalCost) => {
    const costAmount = parseFloat(totalCost.replace('$', ''));
    
    if (costAmount < 500) {
      return {
        totalPayments: 2,
        paymentAmounts: [150, costAmount - 150],
        paymentDescriptions: [
          'Initial payment to commence work',
          'Final payment to release documents'
        ]
      };
    } else {
      const remaining = costAmount - 150;
      const progressPayment = remaining / 2;
      const finalPayment = remaining - progressPayment;
      
      return {
        totalPayments: 3,
        paymentAmounts: [150, progressPayment, finalPayment],
        paymentDescriptions: [
          'Initial payment to commence work',
          'Progress payment for preliminary documents',
          'Final payment to release complete documentation'
        ]
      };
    }
  };

  const getStage2Pricing = (type, variation) => {
    const pricingMap = {
      'single': {
        1: { cost: '$399', revisionFee: 99, conceptTimeline: 4, revisionTimeline: 2 },
        2: { cost: '$550', revisionFee: 150, conceptTimeline: 5, revisionTimeline: 3 },
        3: { cost: '$650', revisionFee: 150, conceptTimeline: 6, revisionTimeline: 3 }
      },
      'duplex': {
        1: { cost: '$399', revisionFee: 99, conceptTimeline: 4, revisionTimeline: 2 },
        2: { cost: '$550', revisionFee: 150, conceptTimeline: 5, revisionTimeline: 3 },
        3: { cost: '$650', revisionFee: 150, conceptTimeline: 6, revisionTimeline: 3 }
      },
      'townhouse': {
        '3-5': { cost: '$799', revisionFee: 150, conceptTimeline: 6, revisionTimeline: 3 },
        '6-9': { cost: '$1099', revisionFee: 150, conceptTimeline: 8, revisionTimeline: 3 }
      },
      'secondary': {
        default: { cost: '$299', revisionFee: 99, conceptTimeline: 4, revisionTimeline: 2 }
      },
      'minor-renovation': {
        default: { cost: '$299', revisionFee: 99, conceptTimeline: 4, revisionTimeline: 2 }
      },
      'major-renovation': {
        1: { cost: '$349', revisionFee: 99, conceptTimeline: 5, revisionTimeline: 2 },
        2: { cost: '$449', revisionFee: 99, conceptTimeline: 6, revisionTimeline: 2 }
      }
    };

    if (type === 'single' || type === 'duplex' || type === 'major-renovation') {
      return pricingMap[type][variation] || pricingMap[type][1];
    } else if (type === 'townhouse') {
      return pricingMap[type][variation] || pricingMap[type]['3-5'];
    } else {
      return pricingMap[type]?.default || { cost: '$299', revisionFee: 99, conceptTimeline: 4, revisionTimeline: 2 };
    }
  };

  const getStage3RevisionPricing = (type, storeys = 1) => {
    if (type === 'single' || type === 'duplex') {
      return storeys === 1 ? 99 : 150;
    } else if (type === 'townhouse') {
      return 150;
    } else if (type === 'secondary' || type === 'minor-renovation' || type === 'major-renovation') {
      return 99;
    }
    return 150;
  };

  const getStage4RevisionPricing = () => {
    return 180;
  };

  const getStage2Timeline = (type, variation) => {
    const pricing = getStage2Pricing(type, variation);
    const conceptDays = pricing.conceptTimeline;
    
    if (conceptDays <= 4) return '3-5 business days';
    if (conceptDays <= 5) return '4-6 business days';
    if (conceptDays <= 6) return '5-7 business days';
    if (conceptDays <= 8) return '7-9 business days';
    return '8-10 business days';
  };

  const getStage2RevisionTimeline = (type, variation) => {
    const pricing = getStage2Pricing(type, variation);
    const revisionDays = pricing.revisionTimeline;
    
    if (revisionDays <= 2) return '1-2 business days';
    if (revisionDays <= 3) return '2-3 business days';
    return '3-5 business days';
  };

  const initializeDocumentRevisionTracking = (stage) => {
    const docTypes = stage === 3 ? documentTypes.stage3 : documentTypes.stage4;
    const tracking = {};
    
    Object.keys(docTypes).forEach(docType => {
      tracking[docType] = {
        revisionsUsed: 0,
        maxIncluded: 1,
        documents: []
      };
    });
    
    return tracking;
  };
  const initializeStage34RevisionTracking = (projectType, quoteDetails = {}) => {
  const stage3Defaults = {
    'architectural': { maxIncluded: 2, customFee: 150 },
    'structural': { maxIncluded: 1, customFee: 0 },
    'hydraulic': { maxIncluded: 1, customFee: 0 },
    'basix': { maxIncluded: 1, customFee: 0 },
    'survey': { maxIncluded: 1, customFee: 0 },
    'geotechnical': { maxIncluded: 1, customFee: 0 },
    'arborist': { maxIncluded: 1, customFee: 0 },
    'heritage': { maxIncluded: 1, customFee: 0 },
    'flood': { maxIncluded: 1, customFee: 0 },
    'bushfire': { maxIncluded: 1, customFee: 0 },
    'see': { maxIncluded: 1, customFee: 0 },
    'consultant-agreements': { maxIncluded: 1, customFee: 0 },
    'application-package': { maxIncluded: 1, customFee: 0 },
    'other': { maxIncluded: 1, customFee: 0 }
  };
  
  const stage4Defaults = {
    'architectural-detailed': { maxIncluded: 2, customFee: 180 },
    'structural-detailed': { maxIncluded: 1, customFee: 0 },
    'electrical': { maxIncluded: 1, customFee: 0 },
    'hydraulic-detailed': { maxIncluded: 1, customFee: 0 },
    'landscape': { maxIncluded: 1, customFee: 0 },
    'construction-details': { maxIncluded: 1, customFee: 0 },
    'joinery-details': { maxIncluded: 1, customFee: 0 },
    'wet-areas-details': { maxIncluded: 1, customFee: 0 },
    'window-door-schedule': { maxIncluded: 1, customFee: 0 },
    'material-finishes-fixtures': { maxIncluded: 1, customFee: 0 },
    'construction-documentation-package': { maxIncluded: 1, customFee: 0 },
    'consultant-agreements': { maxIncluded: 1, customFee: 0 },
    'specifications': { maxIncluded: 1, customFee: 0 },
    'other': { maxIncluded: 1, customFee: 0 }
  };
  
  const defaults = quoteDetails.stage === 4 ? stage4Defaults : stage3Defaults;
  const tracking = {};
  
  Object.keys(defaults).forEach(docType => {
    tracking[docType] = {
      maxIncluded: quoteDetails[`${docType}Revisions`] || defaults[docType].maxIncluded,
      revisionsUsed: 0,
      documents: [],
      customFee: quoteDetails[`${docType}RevisionFee`] || defaults[docType].customFee
    };
  });
  
  return tracking;
};

  const getDocumentRevisionFee = (stage, documentType, revisionNumber, projectData) => {
  // Check if project has custom revision fees set in the quote
  if (projectData?.documentRevisionTracking?.[documentType]?.customFee !== undefined) {
    const tracking = projectData.documentRevisionTracking[documentType];
    if (revisionNumber > tracking.maxIncluded) {
      return tracking.customFee;
    }
    return 0;
  }
  
  // Default fees as fallback
  if (revisionNumber <= 1) return 0;
  
  const fees = {
    stage3: {
      'architectural': 150,
      'structural': 0,
      'hydraulic': 0,
      'basix': 0,
      'survey': 0,
      'geotechnical': 0,
      'arborist': 0,
      'heritage': 0,
      'flood': 0,
      'bushfire': 0,
      'see': 0,
      'consultant-agreements': 0,
      'application-package': 0,
      'other': 0
    },
    stage4: {
      'architectural-detailed': 180,
      'structural-detailed': 0,
      'electrical': 0,
      'hydraulic-detailed': 0,
      'landscape': 0,
      'construction-details': 0,
      'joinery-details': 0,
      'wet-areas-details': 0,
      'window-door-schedule': 0,
      'material-finishes-fixtures': 0,
      'construction-documentation-package': 0,
      'consultant-agreements': 0,
      'specifications': 0,
      'other': 0
    }
  };
  
  const stageFees = stage === 3 ? fees.stage3 : fees.stage4;
  return stageFees[documentType] || 0;
};

  const generatePaymentMessages = (project) => {
    const messages = [];
    const paymentsMade = project.paymentsMade || 0;
    
    if (paymentsMade === 0) return messages;
    
    if (project.stage === 1 && paymentsMade >= 1) {
      messages.push({
        type: 'system',
        sender: 'planAIdesign System',
        message: `Payment 1 of 2 received: $${project.paymentStructure?.paymentAmounts[0] || 99}`,
        timestamp: '2025-08-14 06:00'
      });
    }
    
    return messages;
  };

  // FIXED: Add console.log to verify documentTypes is available
  console.log('documentTypes available in context:', !!documentTypes);
  console.log('documentTypes.stage3 keys:', documentTypes.stage3 ? Object.keys(documentTypes.stage3) : 'undefined');

  const value = {
    // User state
    currentUser,
    setCurrentUser,
    projects,
    setProjects,
    authLoading,
    
    // UI state
    showLanding,
    setShowLanding,
    showAuthModal,
    setShowAuthModal,
    showProjectModal,
    setShowProjectModal,
    showPaymentModal,
    setShowPaymentModal,
    showRevisionModal,
    setShowRevisionModal,
    showRevisionPaymentModal,
    setShowRevisionPaymentModal,
    showRevisionRequestModal,
    setShowRevisionRequestModal,
    showUploadFormsModal,
    setShowUploadFormsModal,
    showAdminPanel,
    setShowAdminPanel,
    showSampleViewer,
    setShowSampleViewer,
    showFeasibilityViewer, 
    setShowFeasibilityViewer,
    authMode,
    setAuthMode,
    
    // Project state
    paymentProject,
    setPaymentProject,
    revisionProject,
    setRevisionProject,
    revisionRequestProject,
    setRevisionRequestProject,
    uploadFormsProject,
    setUploadFormsProject,
    revisionData,
    setRevisionData,
    
    // Notifications
    notifications,
    setNotifications,
    
    // In-app messaging
    inAppMessages,
    dismissMessage,
    
    // Constants and helpers
    developmentSettings,
    pricing,
    typeNames,
    documentTypes, // FIXED: This was missing from the value object!
    getStage2Pricing,
    getStage3RevisionPricing,
    getStage4RevisionPricing,
    getStage2Timeline,
    getStage2RevisionTimeline,
    calculateStage1PaymentStructure,
    calculateStage2PaymentStructure,
    initializeDocumentRevisionTracking,
    initializeStage34RevisionTracking,
    getDocumentRevisionFee,
    generatePaymentMessages,
    isAdminUser,
    STAGE_3_4_ENABLED,
    
    // Functions
    showNotification,
    logout,
    processPayment,
    processStage34Payment,
    approveProject,
    requestRevision,
    requestAdditionalRevision,
    uploadCompletedForms
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};